import React from 'react';
import PropTypes from 'prop-types';
import ALink from 'tradera-components/alink/alink';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { ITEM_CARD_IMAGE_MODES } from './item-card-parts/item-card-image-modes';
import AnimatedPriceLabel from './item-card-parts/animated-price-label';
import EndTimeLabel from './item-card-parts/end-time-label';
import AttributeButtonsList from './item-card-parts/attribute-buttons-list';
import BidsLabel from './item-card-parts/bids-label';
import DiscountBadge from './item-card-parts/discount-badge';
import DiscountedPriceLabel from './item-card-parts/disacount-price-label';
import FixedPriceLabel from './item-card-parts/fixed-price-label';
import ItemCardImage from './item-card-parts/item-card-image';
import LeadingBadge from './item-card-parts/leading-badge';
import NewTodayLabel from './item-card-parts/new-today-label';
import OnDisplayBadge from './item-card-parts/on-display-badge';
import OverbiddenBadge from './item-card-parts/overbidden-badge';
import PriceLabel from './item-card-parts/price-label';
import QuickViewButton from './item-card-parts/quick-view-button';
import SettingsButton from './item-card-parts/settings-button';
import ShippingBadge from './item-card-parts/shipping-badge';
import WishListButton from './item-card-parts/wish-list-button';
import SellerAliasBadge from './item-card-parts/seller-alias-badge';
import { useSelector } from 'react-redux';
import { selectIsSwedishVisitor } from 'tradera-state/shipping-region/selectors';
import ITEM_TYPES from 'tradera-constants/item-types';
import { useDayJs } from 'tradera-hooks/use-day-js';
import { useUrlLocalizer } from 'tradera-lang/translate';

const ItemCard = ({
    animatePriceLabel,
    className,
    gaCategory,
    imageMode,
    item = {},
    lazy,
    onAddToWishlist,
    onQuickView,
    onRemoveFromWishlist,
    onAttributeButtonClick,
    onSettings,
    ready,
    showBidStatus,
    showShippingOrOnDisplay,
    showSellerAlias,
    isShopOwnedByCurrentMember = false
}) => {
    let inSPA = true;
    const existsInWishList = useSelector(state =>
        state.items.wishListItemIds.includes(item.itemId)
    );
    const isSwedishVisitor = useSelector(selectIsSwedishVisitor);
    const { toLocalizedUrl } = useUrlLocalizer();
    const dayjs = useDayJs();

    ///////
    const showBids = [ITEM_TYPES.AUCTION, ITEM_TYPES.AUCTION_BIN].includes(
        item.itemType
    );
    const isShopItem = item.itemType === ITEM_TYPES.SHOP_ITEM;
    const hasDiscount = item.discountRate > 0;

    const isNewToday =
        !!item.startDate &&
        dayjs(item.startDate).isAfter(dayjs().subtract(1, 'day'));
    const itemUrl = toLocalizedUrl(item.itemUrl || '/');
    ///////

    const getMediumFitImageUrl = imageUrl =>
        imageUrl?.replace('/medium/', '/medium-fit/');

    const handleSettings = event => {
        event.preventDefault();
        onSettings(item);
    };

    const handleQuickview = event => {
        event.preventDefault();
        onQuickView(item.itemId);
    };

    const handleWishlist =
        onRemoveFromWishlist && onAddToWishlist
            ? event => {
                  event.preventDefault();
                  if (existsInWishList) {
                      onRemoveFromWishlist(item.itemId);
                  } else {
                      onAddToWishlist(item.itemId);
                  }
              }
            : null;

    const onNavigate = () => {
        if (gaCategory) {
            GtmService.trackAction(gaCategory, 'Navigation', item.itemId);
        }
    };

    return (
        <div
            id={`item-card-${item.itemId}`}
            data-item-loaded={ready ? 'true' : 'false'}
            className={[
                'item-card-new',
                ready ? '' : 'item-card-loading',
                item.displayHighlighted ? 'item-card-new-highlight' : '',
                className
            ].join(' ')}>
            <div className="item-card-inner-wrapper">
                <ItemCardImage
                    className="mb-2 cursor-pointer"
                    onClick={onNavigate}
                    imageHeight={item.imageHeight}
                    imageMode={imageMode}
                    imageUrl={getMediumFitImageUrl(item.imageUrl)}
                    imageWidth={item.imageWidth}
                    imageSecondaryUrl={getMediumFitImageUrl(
                        item.imageSecondaryUrl
                    )}
                    itemUrl={itemUrl}
                    lazy={lazy}
                    inSPA={inSPA}
                    shortDescription={item.shortDescription}>
                    <div className="item-card-top-badges">
                        {showSellerAlias && (
                            <SellerAliasBadge sellerAlias={item.sellerAlias} />
                        )}
                    </div>
                    <div className="item-card-bottom-badges">
                        {showShippingOrOnDisplay && isSwedishVisitor && (
                            <ShippingBadge
                                className="mt-2 mr-2"
                                hasFreeShipping={item.hasFreeShipping}
                            />
                        )}
                        {showShippingOrOnDisplay && item.onDisplay && (
                            <OnDisplayBadge className="mt-2 mr-2" />
                        )}
                        {showBidStatus && item.isLeading && (
                            <LeadingBadge className="mt-2 float-right" />
                        )}
                        {showBidStatus && item.isOverbidden && (
                            <OverbiddenBadge className="mt-2 float-right" />
                        )}
                    </div>
                </ItemCardImage>
                <div className="item-card-buttons d-flex flex-column">
                    {ready && (
                        <>
                            {handleWishlist && (
                                <WishListButton
                                    className={`item-card-wishlist-button btn-md-sm ${
                                        existsInWishList
                                            ? 'item-card-wishlist-button-active'
                                            : ''
                                    } mb-1`}
                                    disabled={!ready}
                                    onClick={handleWishlist}
                                    showText={false}
                                    toggled={existsInWishList}
                                />
                            )}
                            {onQuickView && (
                                <QuickViewButton
                                    className="item-card-quickview-button btn btn-md-sm btn-translucent mb-1 d-none d-lg-inline-block"
                                    onClick={handleQuickview}
                                />
                            )}
                            {isShopOwnedByCurrentMember && (
                                <SettingsButton
                                    className="item-card-settings-button btn btn-md-sm btn-translucent mb-1"
                                    data-shop-item-settings-action
                                    onClick={handleSettings}
                                />
                            )}
                        </>
                    )}
                </div>
                <p className="item-card-title d-flex flex-row mb-1">
                    <ALink
                        onClick={onNavigate}
                        className="font-weight-normal text-truncate-two-lines"
                        href={itemUrl}
                        id={`item_${item.itemId}`}
                        role="button"
                        title={item.shortDescription}
                        useLink={inSPA}>
                        {hasDiscount && (
                            <DiscountBadge
                                className="mr-2 badge-inline"
                                discountRate={item.discountRate}
                            />
                        )}
                        {item.shortDescription}
                    </ALink>
                </p>
                {!!item.attributes?.length && onAttributeButtonClick && (
                    <AttributeButtonsList
                        className="mb-1"
                        attributes={item.attributes}
                        onClick={onAttributeButtonClick}
                    />
                )}
                <div className="item-card-details">
                    {hasDiscount && (
                        <DiscountedPriceLabel
                            className="d-inline-block text-nowrap mr-2"
                            price={item.priceBeforeDiscount}
                        />
                    )}
                    {animatePriceLabel ? (
                        <AnimatedPriceLabel
                            className="d-inline-block text-nowrap font-weight-bold item-card-details-price"
                            price={item.price}
                            hasDiscount={hasDiscount}
                        />
                    ) : (
                        <PriceLabel
                            className="d-inline-block text-nowrap font-weight-bold item-card-details-price"
                            price={item.price}
                            hasDiscount={hasDiscount}
                        />
                    )}
                    {isShopItem ? (
                        <FixedPriceLabel className="d-inline-block text-nowrap float-right" />
                    ) : (
                        <EndTimeLabel
                            className="d-inline-block text-nowrap float-right"
                            endDate={item.endDate}
                            isActive={item.isActive}
                        />
                    )}

                    {showBids && (
                        <BidsLabel
                            className="d-inline-block text-nowrap mr-2"
                            count={item.totalBids}
                        />
                    )}
                    {isNewToday && (
                        <NewTodayLabel className="d-inline-block text-nowrap mr-2" />
                    )}
                </div>
            </div>
        </div>
    );
};

ItemCard.defaultProps = {
    animatePriceLabel: false,
    className: '',
    gaCategory: null,
    imageMode: ITEM_CARD_IMAGE_MODES.SQUARE,
    onQuickView: null,
    showBidStatus: false,
    showShippingOrOnDisplay: true,
    showSellerAlias: false
};

ItemCard.propTypes = {
    animatePriceLabel: PropTypes.bool,
    className: PropTypes.string,
    gaCategory: PropTypes.string,
    imageMode: PropTypes.oneOf(Object.values(ITEM_CARD_IMAGE_MODES)),
    item: PropTypes.shape({
        attributes: PropTypes.array,
        buyNowPrice: PropTypes.number,
        discountRate: PropTypes.number,
        displayHighlighted: PropTypes.bool,
        endDate: PropTypes.string,
        hasFreeShipping: PropTypes.bool,
        imageHeight: PropTypes.number,
        imageUrl: PropTypes.string,
        imageSecondaryUrl: PropTypes.string,
        imageWidth: PropTypes.number,
        isActive: PropTypes.bool,
        itemId: PropTypes.number,
        itemType: PropTypes.string,
        itemUrl: PropTypes.string,
        onDisplay: PropTypes.bool,
        price: PropTypes.number,
        priceBeforeDiscount: PropTypes.number,
        reservePriceReached: PropTypes.bool,
        sellerAlias: PropTypes.string,
        sellerDsrAverage: PropTypes.number,
        sellerMemberId: PropTypes.number,
        shippingOptions: PropTypes.array,
        shortDescription: PropTypes.string,
        startDate: PropTypes.string,
        totalBids: PropTypes.number,

        /* Currently only used in Wish list */
        isOverbidden: PropTypes.bool,
        isLeading: PropTypes.bool,
        isWinner: PropTypes.bool
    }),
    lazy: PropTypes.shape({
        offset: PropTypes.number,
        once: PropTypes.bool,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        placeholder: PropTypes.node,
        imagePlaceholder: PropTypes.node
    }),
    onAddToWishlist: PropTypes.func,
    onRemoveFromWishlist: PropTypes.func,
    onQuickView: PropTypes.func,
    onSettings: PropTypes.func,
    ready: PropTypes.bool,
    showBidStatus: PropTypes.bool,
    showShippingOrOnDisplay: PropTypes.bool,
    isShopOwnedByCurrentMember: PropTypes.bool
};

export default ItemCard;
